import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as CheckoutContext } from '../context/checkout';
import { Context as AuthContext } from '../context/auth';
import { Context as RestaurantContext } from '../context/restaurant';
import MainLayout from '../layouts/mainLayout';
import TopBar from '../layouts/place/topBar';
import SuccessPayment from '../features/payments/successPayment';
import Loader from '../components/misc/loader';
import FailPayment from '../features/payments/failPayment';
import { restaurantTypes } from '../enums/restaurantTypes';
import { useTableSseConnection } from '../hooks/useTableSseConnection';
import { usePaymentHandler } from '../hooks/usePaymentHandler';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useTableSseConnection();

  const { rateOrder } = useContext(CheckoutContext);
  const { state: authState } = useContext(AuthContext);
  const { state: restaurantState } = useContext(RestaurantContext);

  const { isCanceled, isLoading, isProcessing, paymentData } = usePaymentHandler({
    holdProcessing: false,
  });

  useEffect(() => {
    // Cancelled payment
    if (isCanceled) {
      if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
        navigate('/cart', { replace: true });
      } else if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
        navigate('/', { replace: true });
      } else {
        navigate('/bill', { replace: true });
      }
    }
  }, []);

  // Cancelled payment
  if (isCanceled) {
    return null;
  }

  // Loading payment
  if (isLoading) {
    return <Loader />;
  }

  // Waiting for confirmation from server
  if (isProcessing) {
    return (
      <MainLayout>
        <div className="sticky top-0 bg-white z-10">
          <TopBar
            title={t('header.payment')}
            onBack={() => {
              if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
                navigate('/orders');
              } else if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
                navigate('/');
              } else {
                navigate('/bill');
              }
            }}
          />
        </div>
        <Loader text={t('checkout.processingPaymentLoader')} />
      </MainLayout>
    );
  }

  if (!paymentData) {
    return (
      <MainLayout>
        <div className="sticky top-0 bg-white z-10">
          <TopBar
            title={t('header.payment')}
            onBack={() => {
              if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
                navigate('/orders');
              } else if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
                navigate('/');
              } else {
                navigate('/bill');
              }
            }}
          />
        </div>
        <div className="mx-auto mt-20">
          <h1 className="font-primaryMedium text-2xl text-primary text-center">
            {t('errors.noPayment')}
          </h1>
        </div>
      </MainLayout>
    );
  }

  const updateRating = (rating, comment) => {
    rateOrder({
      token: authState.token,
      orderId: paymentData.orderId,
      paymentId: paymentData.id,
      userId: authState.user.id,
      restaurantId: restaurantState.restaurant.id,
      rating,
      comment,
    });
  };

  return (
    <MainLayout>
      <div className="sticky top-0 bg-white z-10">
        <TopBar
          title={t('header.payment')}
          onBack={() => {
            if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
              navigate('/orders');
            } else if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
              navigate('/');
            } else {
              navigate('/bill');
            }
          }}
        />
      </div>
      {paymentData && paymentData.status === 'Paid' && paymentData.processed && (
        <SuccessPayment
          payment={paymentData}
          canRate={authState.user && authState.user.id && authState.token}
          onUpdateRating={updateRating}
          reviewLink={restaurantState.restaurant.reviewLink}
        />
      )}
      {paymentData && paymentData.status !== 'Paid' && paymentData.processingFailed && (
        <FailPayment payment={paymentData} />
      )}
    </MainLayout>
  );
};

export default PaymentPage;
